import type { CountryCode, Locale } from '$lib/i18n/types';
import { browser } from '$app/environment';
import { getCountryCode } from '$lib/i18n/locale';
import { deviceType } from '$lib/services/mediaQuery/screenSize';
import { get } from 'svelte/store';
import { setupGoogleAds } from '../google';
import { addScriptTag } from '../script';
import { REWORLD_CLIENT_ID } from './constants';

export function getReworldAdUnit(locale: Locale = 'en-US') {
	const host = getReworldViamichelinHost(getCountryCode(locale));

	return `/${REWORLD_CLIENT_ID}/${host}-web`;
}

function getReworldViamichelinHost(country: CountryCode) {
	const mapping: Record<CountryCode, string> = {
		AT: 'viamichelin.at',
		BE: 'viamichelin.be',
		CH: 'viamichelin.ch',
		DE: 'viamichelin.de',
		ES: 'viamichelin.es',
		GB: 'viamichelin.co.uk',
		NL: 'viamichelin.nl',
		PT: 'viamichelin.pt',
		US: 'viamichelin.com',
		IE: 'viamichelin.ie',
		FR: '', // Handled by a different ad provider
		IT: '', // Handled by a different ad provider
	};

	return mapping[country];
}

function getBootPbstckId(country: CountryCode) {
	const mapping: Record<CountryCode, string> = {
		AT: '3c1e7eff-9b73-4f5a-9ea0-c7d618e6a5d9',
		BE: '',
		CH: '0e617990-82eb-463d-a6c3-3d8e1e0e89c3',
		DE: '242c7502-f319-4179-93aa-4ca87223c9bd',
		ES: 'f2e90f2f-82f3-425d-9db8-ffd0afc48adf',
		GB: '64e7b6b6-9b7c-4880-9aff-2d2c468044da',
		NL: '2e655a02-f784-4e68-9ebb-4b8172811447',
		PT: '',
		US: '',
		IE: 'daa2656d-90eb-4c01-b881-bf5c4d6d622b',
		FR: '',
		IT: '',
	};
	return mapping[country];
}

function addReworldAdScript(locale: Locale) {
	const host = getReworldViamichelinHost(getCountryCode(locale));
	const bootPbstckId = getBootPbstckId(getCountryCode(locale));

	addScriptTag('reworld-ad-script', `https://prebid.reworldmediafactory.com/${host}/script.min.js`, { attributes: { fetchpriority: 'low' } });
	addScriptTag('GAM-script', 'https://securepubads.g.doubleclick.net/tag/js/gpt.js', {
		attributes: {
			'data-vendor-raw': 'didomi:google',
			'fetchpriority': 'low',
		},
	});

	addScriptTag(
		'reword-ad-script-utils',
		'https://prebid.reworldmediafactory.com/pbutils/prebid_utils.min.js',
		{ attributes: { fetchpriority: 'low' } },
	);

	addScriptTag('boot-pbstck', `https://boot.pbstck.com/v1/tag/${bootPbstckId}`, { attributes: { fetchpriority: 'low' } });
}

function setReworldDeviceType() {
	if (!browser) {
		// Safeguard as this code is not meant to run on the server
		return;
	}

	const device = get(deviceType);

	// Reworld expects these to be setup in the global scope
	// See: https://prebidaio.reworldmediafactory.com/doc-site-evol/fr/viamichelin.com/%2F46980923%252C2787961%2Fviamichelin.com?habillage=0&oneView=1&cmp=didomi&prebidUtils=1&safeFrame=1
	window.is_mobile = device === 'mobile';
	window.is_desktop = device === 'desktop';
	window.is_tablet = device === 'tablet';
}

export function setupReworldAds(locale: Locale) {
	setReworldDeviceType();

	// Reworld ads work in tandem with Google ads
	setupGoogleAds();

	window.reworldAd = window.reworldAd || { que: [] };
	window.reworldAdUtils = window.reworldAdUtils || { que: [] };

	const adUnit = getReworldAdUnit(locale);

	window.reworldAd.que.push(() => {
		window.reworldAdUtils?.que.push(() => {
			window.reworldAdUtils?.setPrebidUtilsConfig?.({ adUnit });
		});
	});

	addReworldAdScript(locale);
}
